/**
 * P2P seeding-activity surface for the v3 instant-mode success screen (ticket #20).
 *
 * `PeerTransfer` owns the WebRTC data channels but sits on top of `@kitecorp/web-peer`, which must
 * never be imported at module scope by UI components (private runtime, SSR-stubbed in
 * vite.config.ts, uninstallable in tests). The observable state is therefore extracted into this
 * dependency-free leaf module: PeerTransfer reports channel opens/closes, send starts/ends and the
 * signaling socket's drops and connects into the hub, and the UI subscribes through
 * `TransferService.onPeerActivity` using only these types.
 */

import {ConnectionReading, totalSpeed} from './connectionReadout';
import {TransferTerminationReason} from './transferTermination';

/**
 * Opaque per-channel identity. The hub never touches a channel, only counts what happens on one, so
 * this is deliberately `object` rather than `RTCDataChannel`: the owner passes its own channel and this
 * module stays free of the WebRTC types it must not import (see the header).
 */
export type PeerChannelKey = object;

/** Live snapshot of the instant-transfer seeding session. */
export interface PeerActivity {
	/**
	 * Name of a seeded file that became unreadable on disk (ui#123 — e.g. the source was
	 * overwritten while seeding; Chromium invalidates the File snapshot and every read fails).
	 * Null while seeding is healthy. The UI turns the name into localized copy.
	 */
	seedingError: string | null;
	/**
	 * Why this transfer stopped serving for good (kitecorp/transferify-api-service#418): the api
	 * suspended it over abuse reports, or its owner deleted it. Null while it is still live.
	 *
	 * Terminal and distinct from an empty {@link recipients} list, which only means nobody happens to be
	 * connected right now. The success screen must say the link is dead rather than leave the user waiting
	 * for recipients who can never arrive.
	 */
	terminated: TransferTerminationReason | null;
	/**
	 * How many times a recipient has received a WHOLE file from this sender (ui#1665).
	 *
	 * Latched, never decremented: it counts completions, and a completion cannot be taken back. That is
	 * the point of the field — the fact it reports is about what has already happened, so neither the
	 * channel going quiet nor the recipient vanishing may erase it.
	 *
	 * It counts SENDS, not recipients: one recipient asking for three files raises it three times, and
	 * one asking for the same file twice raises it twice. That is the honest reading of the signal the
	 * protocol gives us: the recipient's receipt for the file, or for a recipient that announces no
	 * receipts the file queued whole (kitecorp/transferify-ui#3331). The surfaces that read it either
	 * compare it to zero or state the count plainly.
	 */
	servedDownloads: number;
	/**
	 * How far the FIRST recipient of this transfer has got (kitecorp/transferify-ui#2872), or null
	 * until one has asked for files. Byte Runner's finish line in the live card follows it.
	 *
	 * The first recipient is the first channel that requested files in this session, and the record is
	 * latched: it outlives that channel closing, because the game's rail must not jump back to zero when
	 * a recipient's connection drops mid-download. Bytes count when the sender has queued them on the
	 * channel, and `complete` only once every requested file was served, which is the same moment
	 * {@link servedDownloads} counts a download. So the game can never show completion first.
	 */
	firstRecipient: FirstRecipientProgress | null;
	/**
	 * Everyone the live card lists (kitecorp/transferify-ui#3320), in the order they opened the link: every
	 * recipient whose channel is open, and every recipient that received at least one whole file, whose row
	 * stays once they leave because the download it reports has happened. A recipient that leaves having
	 * received nothing drops out; its number is never given to anyone else.
	 */
	recipients: RecipientActivity[];
	/**
	 * True while this tab's signaling socket, having connected, is down and web-peer has a reconnect scheduled
	 * (kitecorp/transferify-ui#3383). No new recipient can reach the sender until it connects again, so the live card
	 * says so; recipients already connected keep downloading over their own peer connections, which signaling does not
	 * carry.
	 *
	 * Never true for a socket that has not connected yet: stompjs reports every failed FIRST attempt as a drop with a
	 * reconnect scheduled too, and with no signaling server at all (the mocked battery) that replaced the live title
	 * on every card. False again on the next connect, and after a drop that nothing will retry: then there is no
	 * reconnect to wait for, and the card keeps its normal state. Kept across {@link PeerActivityHub.reset}, because
	 * the socket belongs to the tab, not to one transfer.
	 */
	reconnecting: boolean;
}

/** What one recipient is doing; see {@link RecipientActivity.state}. */
export enum RecipientState {
	/** Connected, and has asked for nothing yet (or nothing it asked for is still on its way). */
	Opened = "opened",
	/** Has files on their way: streaming now, or asked for and not yet sent. */
	Downloading = "downloading",
	/** Received every file it asked for, at least one of them whole. */
	Finished = "finished",
}

/** One row of the live card's recipient list; see {@link PeerActivity.recipients}. */
export interface RecipientActivity {
	/**
	 * 1, 2, 3… in the order recipients opened the link in this session, and fixed for the session: a channel is
	 * opaque, so the number is the only name a row has, and it must not move to someone else.
	 */
	number: number;
	state: RecipientState;
	/** The bytes of every file this recipient asked for and is still being sent or has received. */
	requestedBytes: number;
	/** Of those, the bytes already queued on its channel. */
	sentBytes: number;
	/** How fast its connections move bytes together, or null until one has been read twice. */
	bytesPerSecond: number | null;
}

/** See {@link PeerActivity.firstRecipient}. */
export interface FirstRecipientProgress {
	sentBytes: number;
	requestedBytes: number;
	complete: boolean;
}

export type PeerActivityListener = (activity: PeerActivity) => void;

export type Unsubscribe = () => void;

/**
 * A send in flight, tagged with the session it started in.
 *
 * {@link PeerActivityHub.reset} means "a DIFFERENT transfer starts now", and a send can still be
 * streaming when it happens (`TransferService.instantUpload` resets before every registration, and a
 * termination tears the session down under an active stream). Without the tag, that send resolving
 * afterwards would latch a completion onto the new transfer, and the next success screen would open
 * already claiming a download nobody made — with the subscription offer that rides on it.
 */
export interface PeerSendToken {
	readonly channel: PeerChannelKey;
	readonly session: number;
	/** The file's size, so a completed send can count exactly what it delivered. */
	readonly bytes: number;
}

/** One recipient channel's running totals for this session; see {@link PeerActivity.recipients}. */
interface RecipientRecord {
	readonly number: number;
	/** Cleared when the channel closes; the record stays, because a finished row outlives its channel. */
	open: boolean;
	requestedBytes: number;
	/** Bytes of files served: confirmed by the recipient's receipt, or queued whole for one that sends none. */
	servedBytes: number;
	/**
	 * The bytes each send in flight on this channel has queued: one entry per file that is streaming, or queued and not
	 * yet answered by the recipient's receipt (kitecorp/transferify-ui#3331). Several files of one request can wait for
	 * their receipts while the next one streams.
	 */
	streaming: Map<PeerSendToken, number>;
	/** Whole files this channel has received. */
	served: number;
	/**
	 * A file it asked for failed and it has asked for nothing since. Its row still reports what arrived, but its
	 * download is not complete: the game's finish line and the completion report wait for the retry.
	 */
	failedSinceRequest: boolean;
	/** The whole percentage last announced, so a chunk that moves nothing notifies nobody. */
	announcedPercent: number;
	/** The last readings of this recipient's connections, one per peer connection. */
	connections: ConnectionReading[];
}

/** The bytes that reached the recipient whole, plus what each of its sends in flight has queued. */
function sentBytesOf(record: RecipientRecord): number {
	let sent = record.servedBytes;
	record.streaming.forEach(bytes => sent += bytes);
	return sent;
}

function percentOf(record: RecipientRecord): number {
	return record.requestedBytes === 0 ? 0 : Math.floor(100 * sentBytesOf(record) / record.requestedBytes);
}

/**
 * The state ui#1665 is about. "Downloading" is "files on their way", never "connected": a finished
 * recipient's channel stays open (the recipient's page keeps it for further requests, and a destroyed
 * browser context does not even close it, there being no graceful SCTP shutdown), so a connection count
 * would keep calling a finished download a running one.
 */
function stateOf(record: RecipientRecord): RecipientState {
	if (record.streaming.size > 0 || record.requestedBytes > record.servedBytes) {
		return RecipientState.Downloading;
	}
	return record.served > 0 ? RecipientState.Finished : RecipientState.Opened;
}

/** A listed record as its row; a recipient that has left reports only the files that reached it whole. */
function rowOf(record: RecipientRecord): RecipientActivity {
	if (!record.open) {
		return {
			number: record.number, state: RecipientState.Finished,
			requestedBytes: record.servedBytes, sentBytes: record.servedBytes, bytesPerSecond: null,
		};
	}
	return {
		number: record.number,
		state: stateOf(record),
		requestedBytes: record.requestedBytes,
		sentBytes: sentBytesOf(record),
		bytesPerSecond: totalSpeed(record.connections),
	};
}

/**
 * Tracks each recipient channel and fans every change out to subscribers. The current snapshot is
 * replayed immediately on subscribe, so late subscribers (the success screen mounts after
 * registration) never miss an already-connected recipient.
 */
export class PeerActivityHub {
	/** Every recipient of this session in the order it arrived, which is the order a Map iterates in. */
	private readonly recipients = new Map<PeerChannelKey, RecipientRecord>();
	/** Bumped by {@link reset}; see {@link PeerSendToken}. */
	private session = 0;
	private seedingError: string | null = null;
	private terminated: TransferTerminationReason | null = null;
	private servedDownloads = 0;
	/** The channel {@link PeerActivity.firstRecipient} follows. */
	private first: PeerChannelKey | null = null;
	/** See {@link PeerActivity.reconnecting}. */
	private reconnecting = false;
	/** Whether the current socket has connected at least once; a drop before that is a first connect still trying. */
	private signalingEverConnected = false;
	private readonly listeners = new Set<PeerActivityListener>();

	subscribe(listener: PeerActivityListener): Unsubscribe {
		this.listeners.add(listener);
		listener(this.snapshot());
		return () => this.listeners.delete(listener);
	}

	/** Idempotent: an already-tracked channel keeps the seeding history recorded against it. */
	channelOpened(channel: PeerChannelKey): void {
		this.recordOf(channel).open = true;
		this.emit();
	}

	channelClosed(channel: PeerChannelKey): void {
		const record = this.recipients.get(channel);
		if (record === undefined || !record.open) {
			return;
		}
		record.open = false;
		record.connections = [];
		this.emit();
	}

	/**
	 * A file started streaming to `channel`. Tracks an untracked channel: streaming implies open.
	 *
	 * @returns the token {@link sendEnded} must be given back, which carries the session this send
	 *          belongs to.
	 */
	sendStarted(channel: PeerChannelKey, bytes = 0): PeerSendToken {
		const record = this.recordOf(channel);
		const token = {channel, session: this.session, bytes};
		record.streaming.set(token, 0);
		this.emit();
		return token;
	}

	/**
	 * A recipient asked for files totalling `bytes` (kitecorp/transferify-ui#2872). The first channel to
	 * ask in this session becomes the first recipient; every later request adds to what its channel asked for.
	 */
	requested(channel: PeerChannelKey, bytes: number): void {
		const record = this.recordOf(channel);
		this.first ??= channel;
		record.requestedBytes += bytes;
		record.failedSinceRequest = false;
		record.announcedPercent = percentOf(record);
		this.emit();
	}

	/**
	 * `fileBytesSent` of the file behind `token` are queued on its channel. Notifies only when that
	 * recipient's whole percentage moves: this is called per cipher chunk, and a render per chunk would
	 * cost the sender's tab more than the card is worth.
	 */
	progressed(token: PeerSendToken, fileBytesSent: number): void {
		const record = this.recipients.get(token.channel);
		if (token.session !== this.session || record === undefined || !record.streaming.has(token)) {
			return;
		}
		record.streaming.set(token, fileBytesSent);
		const percent = percentOf(record);
		if (percent !== record.announcedPercent) {
			record.announcedPercent = percent;
			this.emit();
		}
	}

	/**
	 * A file stopped streaming; `served` says whether the recipient got the whole of it (ui#1665).
	 *
	 * <b>A SUCCESS is counted even when the channel has closed.</b> Two real orderings reach that state,
	 * and both delivered the file: `withdrawIdentity` closing a channel that was mid-stream on a DIFFERENT
	 * identity, and the recipient's own `onclose`/`onerror` landing between the last byte and the caller's
	 * `finally`. The latch is about what happened, not about who is still connected.
	 *
	 * <b>A failure gives the file's bytes back</b> (kitecorp/transferify-ui#3320): that file is no longer on
	 * its way, so the row stops reading as downloading it, and asking for it again counts it once rather
	 * than twice.
	 *
	 * <b>A completion from a PREVIOUS session is dropped entirely</b> — see {@link PeerSendToken}. It is
	 * not this transfer's download, and this transfer's screen is where it would have been shown.
	 */
	sendEnded(token: PeerSendToken, served: boolean): void {
		if (token.session !== this.session) {
			return;
		}
		const record = this.recipients.get(token.channel);
		if (record !== undefined) {
			record.streaming.delete(token);
			if (served) {
				record.served += 1;
				record.servedBytes += token.bytes;
			} else {
				record.requestedBytes = Math.max(record.servedBytes, record.requestedBytes - token.bytes);
				record.failedSinceRequest = true;
			}
			record.announcedPercent = percentOf(record);
		}
		if (served) {
			this.servedDownloads += 1;
		}
		this.emit();
	}

	/**
	 * The latest readings of `channel`'s connections, one per peer connection (kitecorp/transferify-ui#3316),
	 * or none once its connection set is gone. A reading for a channel that is not open is dropped: it
	 * describes a recipient who has already gone.
	 */
	connectionsRead(channel: PeerChannelKey, readings: ConnectionReading[]): void {
		const record = this.recipients.get(channel);
		if (record === undefined || !record.open) {
			return;
		}
		record.connections = readings;
		this.emit();
	}

	/**
	 * Surfaces a dead seeded file (ui#123) — the success screen shows it instead of the pulsing
	 * "waiting" line, because this sender can no longer serve that file to anyone.
	 */
	reportSeedingError(fileName: string): void {
		this.seedingError = fileName;
		this.emit();
	}

	/**
	 * Marks the transfer terminated (kitecorp/transferify-api-service#418) — the api suspended or
	 * deleted it, so this sender has stopped serving it and no recipient can ever connect again.
	 * Cleared only by {@link reset}, i.e. by starting a different transfer.
	 */
	reportTerminated(reason: TransferTerminationReason): void {
		this.terminated = reason;
		this.emit();
	}

	/**
	 * This tab's signaling socket connected: web-peer's `onConnected`, which fires on the first connect and on every
	 * reconnect after it (kitecorp/transferify-ui#3383).
	 */
	signalingConnected(): void {
		this.signalingEverConnected = true;
		this.setReconnecting(false);
	}

	/**
	 * The socket dropped (kitecorp/transferify-ui#3383). Reconnecting while web-peer has a reconnect scheduled, once
	 * this socket has connected at least once; a drop that nothing will retry (the session was stopped) ends that state
	 * too, since there is nothing left to wait for. Set from each report rather than counted, because web-peer reports
	 * one refusal twice: its ERROR frame, then the close.
	 */
	signalingDisconnected(reconnectScheduled: boolean): void {
		this.setReconnecting(this.signalingEverConnected && reconnectScheduled);
	}

	/**
	 * A new socket replaced the one this hub was told about (identity renewal builds one). Its life starts here: until
	 * it connects, its failed attempts are a first connect, not a reconnect, whatever the old socket had done.
	 */
	signalingReplaced(): void {
		this.signalingEverConnected = false;
		this.setReconnecting(false);
	}

	/**
	 * Empties the session and notifies subscribers — a NEW instant transfer starts with no recipients
	 * (ui#84: "Send another" kept showing the previous session's count), numbers its own from 1, and has
	 * a clean error surface (ui#123). {@link PeerActivity.reconnecting} stays: it describes the tab's socket.
	 */
	reset(): void {
		this.session += 1;
		this.recipients.clear();
		this.seedingError = null;
		this.terminated = null;
		// Zeroed with the error latch, and for the same ui#84 reason it was added for: a NEW instant
		// transfer has served nobody, so carrying the previous session's completions forward would open
		// the next success screen already claiming a download that belonged to a different set of files.
		this.servedDownloads = 0;
		this.first = null;
		this.emit();
	}

	snapshot(): PeerActivity {
		const first = this.first === null ? undefined : this.recipients.get(this.first);
		return {
			seedingError: this.seedingError,
			terminated: this.terminated,
			servedDownloads: this.servedDownloads,
			firstRecipient: first === undefined ? null : {
				sentBytes: sentBytesOf(first),
				requestedBytes: first.requestedBytes,
				complete: first.requestedBytes > 0 && first.streaming.size === 0 && !first.failedSinceRequest
					&& first.servedBytes >= first.requestedBytes,
			},
			recipients: [...this.recipients.values()]
				.filter(record => record.open || record.served > 0)
				.map(rowOf),
			reconnecting: this.reconnecting,
		};
	}

	/** Notifies only on a change: a socket that stays down reports every failed retry, five seconds apart. */
	private setReconnecting(reconnecting: boolean): void {
		if (reconnecting !== this.reconnecting) {
			this.reconnecting = reconnecting;
			this.emit();
		}
	}

	/** `channel`'s record, created open under the next number when the channel is new to this session. */
	private recordOf(channel: PeerChannelKey): RecipientRecord {
		let record = this.recipients.get(channel);
		if (record === undefined) {
			record = {
				number: this.recipients.size + 1, open: true, requestedBytes: 0, servedBytes: 0, streaming: new Map(),
				served: 0, failedSinceRequest: false, announcedPercent: 0, connections: [],
			};
			this.recipients.set(channel, record);
		}
		return record;
	}

	private emit(): void {
		const snapshot = this.snapshot();
		this.listeners.forEach(listener => listener(snapshot));
	}
}
